import type { SplitterPaneSize } from "@mantine/hooks";

import { getLogger } from "@expert/logging";
import { createSimpleStore } from "@expert/state-utils";

const logger = getLogger({ module: "workspacePanelSize.store" });

export type WorkspacePanelMode = "oncall" | "offcall";

const UNINITIALIZED_AGENT_ID = "---";

const makeKey = (agentId: string, persistId: string, mode: WorkspacePanelMode) => `${agentId}:${persistId}:${mode}`;

interface WorkspacePanelSizeState {
    sizesByKey: Record<string, SplitterPaneSize[]>;
    setSizes: (key: string, sizes: SplitterPaneSize[]) => void;
}

export const useWorkspacePanelSizeStore = createSimpleStore<WorkspacePanelSizeState>({
    devtools: {
        store: "workspacePanelSize",
        name: "workspace/redesigned-layout",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
    },
    persist: {
        name: "workspace-panel-size",
        partialize: (s) => ({ sizesByKey: s.sizesByKey }),
    },
})((set) => ({
    sizesByKey: {},
    setSizes: (key, sizes) => set((state) => ({ sizesByKey: { ...state.sizesByKey, [key]: sizes } })),
}));

export function readWorkspacePanelSizes(
    agentId: string,
    persistId: string,
    mode: WorkspacePanelMode,
): SplitterPaneSize[] | undefined {
    if (!agentId || agentId === UNINITIALIZED_AGENT_ID) return undefined;
    const saved = useWorkspacePanelSizeStore.getState().sizesByKey[makeKey(agentId, persistId, mode)];
    return saved?.length === 2 ? saved : undefined;
}

export function writeWorkspacePanelSizes(
    agentId: string,
    persistId: string,
    mode: WorkspacePanelMode,
    sizes: SplitterPaneSize[],
): void {
    if (!agentId || agentId === UNINITIALIZED_AGENT_ID) return;
    try {
        useWorkspacePanelSizeStore.getState().setSizes(makeKey(agentId, persistId, mode), sizes);
    } catch (error) {
        logger.warn({ error }, "Failed to persist workspace panel sizes");
    }
}
